import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/CombinedAuthContext";
import { usePlans } from "../contexts/PlansContext";
import { useAdmin } from "../contexts/AdminContext";
import { useFeaturedListings } from "../hooks/useFeaturedListings";
import { HOME_HERO, HOW_IT_WORKS, DOUBLOONS } from "../constants/copy";
import {
  SEO,
  PAGE_META,
  StructuredData,
  generateOrganizationSchema,
  GlassSection,
  CinematicHero,
  RotatingFeaturePlate,
  FeaturePlate,
  R5Section,
  R5Card,
} from "../components/ui";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faAnchor,
  faCoins,
  faGift,
  faStore,
  faClock,
  faChartLine,
  faBullhorn,
  faUtensils,
  faSpa,
  faShip,
  faPersonSwimming,
  faBicycle,
  faMapLocationDot,
  faTruckFast,
  faTags,
  faMapMarkedAlt,
  faTicket,
  faCalendarAlt,
  faConciergeBell,
  faChartPie,
  faHandshake,
} from "@fortawesome/free-solid-svg-icons";
import "../styles/HomePage.css";
import FeaturedPropertiesCarousel from "../components/realEstate/FeaturedPropertiesCarousel";
import FeaturedEventsCarousel from "../components/events/FeaturedEventsCarousel";
import MemberResources from "../components/MemberResources";

const PROMOTION_BADGES = {
  premium:   "⚓ Captain's Pick",
  featured:  "★ Featured",
  spotlight: "Spotlight",
  boost:     "Trending",
};

function HomePage() {
  const { isAuthenticated, user, login } = useAuth();
  const { isTraveler, isTravelerPlus, isBusinessOwner, hasPaidSubscription } = usePlans();
  const { isAdmin } = useAdmin();
  const navigate = useNavigate();

  // Live Firestore-backed featured pool (promoted first, then by viewCount)
  const { pool: featuredListings, loading } = useFeaturedListings(5);

  // TEMP DIAGNOSTIC (Workstream A0): inspect what image fields the featured
  // listings actually carry on the homepage.
  useEffect(() => {
    if (!featuredListings || featuredListings.length === 0) return;
    // eslint-disable-next-line no-console
    console.log('[HomePage] featuredListings image fields',
      featuredListings.map((l) => ({
        id: l?.id,
        name: l?.name,
        imagesLen: Array.isArray(l?.images) ? l.images.length : null,
        firstImage: Array.isArray(l?.images) ? l.images[0] : null,
        profileImageUrl: l?.profileImageUrl,
        thumbnailUrl: l?.thumbnailUrl,
      })));
  }, [featuredListings]);

  // Every signed-in user is at least a free Deckhand, so every check here is
  // PAID-only — these decide which club we stop pitching to a viewer, and a
  // free Deckhand is still an upgrade target rather than someone to re-recruit.
  const traveler = typeof isTraveler === "function" ? isTraveler() : false;
  const travelerPlus = typeof isTravelerPlus === "function" ? isTravelerPlus() : false;
  const business = typeof isBusinessOwner === "function" ? isBusinessOwner() : false;
  const hasPaidPlan =
    typeof hasPaidSubscription === "function" ? hasPaidSubscription() : false;
  const isMemberWithSubscription = isAuthenticated && (hasPaidPlan || isAdmin);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 18) return "Good afternoon";
    return "Good evening";
  };

  const getUserDisplayName = () => {
    if (!user) return "";
    if (user.member?.profile?.nickname) return user.member.profile.nickname;
    if (user.member?.email) return user.member.email.split("@")[0];
    return "";
  };

  // --- Hero (V2: discovery-first, free-to-join) ---
  const renderHero = () => {
    const lede = isAuthenticated
      ? `${getGreeting()}, ${getUserDisplayName() || "friend"}. ${HOME_HERO.subheadline}`
      : HOME_HERO.subheadline;

    let plate = null;
    if (isAuthenticated && featuredListings.length > 0) {
      plate = <RotatingFeaturePlate pool={featuredListings} badge="Tonight's Featured" />;
    } else if (!isAuthenticated) {
      plate = (
        <FeaturePlate
          badge="Welcome Aboard"
          title="Join Free as a Traveler"
          meta="Free forever · upgrade only if you want premium perks"
          blurb={HOME_HERO.subnote}
          ctaLabel="Join Free →"
          // Route through /signup so the Terms checkbox is enforced and
          // acceptedTermsVersion is recorded; calling signup() skipped both.
          onCtaClick={() => navigate(HOME_HERO.primaryCta.href)}
          secondaryLabel="Already a member? Log in"
          onSecondaryClick={login}
        />
      );
    }

    return (
      <>
        <CinematicHero
          eyebrow={HOME_HERO.subheadline2}
          title={HOME_HERO.headline}
          dropCap="U"
          lede={lede}
          bar={
            <>
              <strong>Free to join</strong> · local deals, maps, events · upgrade only if you want premium perks
            </>
          }
          featurePlate={plate}
        />
        {(!isAuthenticated || !hasPaidPlan) && (
          <GlassSection className="home-hero-cta">
            <div className="home-hero-cta__row">
              {/* Guests join; signed-in free Deckhands are already members, so
                  they get an upgrade path instead of a second "join" pitch. */}
              {!isAuthenticated ? (
                <Link to={HOME_HERO.primaryCta.href} className="home-cta-primary">
                  {HOME_HERO.primaryCta.label} <FontAwesomeIcon icon={faArrowRight} />
                </Link>
              ) : (
                <Link to="/traveler-passes" className="home-cta-primary">
                  Upgrade your pass <FontAwesomeIcon icon={faArrowRight} />
                </Link>
              )}
              <Link to={HOME_HERO.secondaryCta.href} className="home-cta-secondary">
                {HOME_HERO.secondaryCta.label}
              </Link>
              <Link to={HOME_HERO.tertiaryCta.href} className="home-cta-tertiary">
                {HOME_HERO.tertiaryCta.label}
              </Link>
            </div>
            <p className="home-hero-cta__subnote">{HOME_HERO.subnote}</p>
          </GlassSection>
        )}
      </>
    );
  };

  // --- Member-only "Find adventures on the Treasure Map" CTA ---
  // Shown to signed-in members (or admins) right after the hero, in place
  // of the join-the-Guild strip they no longer see.
  const renderMemberAdventuresCta = () => (
    <GlassSection className="home-member-cta">
      <div className="home-member-cta__inner">
        <span className="home-member-cta__eyebrow">· Ready for your next adventure? ·</span>
        <h2 className="home-member-cta__heading">Find adventures on the Treasure Map</h2>
        <p className="home-member-cta__subnote">
          Browse curated stops, events, and treasure across the islands —
          plot your day from the map and we'll handle the rest.
        </p>
        <div className="home-member-cta__row">
          <Link to="/pirate-map" className="home-cta-primary">
            <FontAwesomeIcon icon={faMapMarkedAlt} /> Open the Map!
          </Link>
          <Link to="/events" className="home-cta-secondary">
            or browse upcoming events
          </Link>
        </div>
      </div>
    </GlassSection>
  );

  // --- How The Guild Works (replaces V1 "welcome / membership cards") ---
  const renderHowItWorksSection = () => (
    <R5Section
      className="home-how-it-works"
      title={HOW_IT_WORKS.title}
      meta="Two audiences, three steps each"
    >
      <div className="home-how-it-works__grid">
        <div className="home-how-it-works__column">
          <h3 className="home-how-it-works__heading">
            <FontAwesomeIcon icon={faMapMarkedAlt} /> {HOW_IT_WORKS.travelers.heading}
          </h3>
          <ol className="home-how-it-works__steps">
            {HOW_IT_WORKS.travelers.steps.map((step, i) => (
              <li key={i}>
                <span className="home-how-it-works__num">{i + 1}</span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
          <Link to="/traveler-passes" className="home-cta-secondary">
            View Premium Passes <FontAwesomeIcon icon={faArrowRight} />
          </Link>
        </div>
        <div className="home-how-it-works__column">
          <h3 className="home-how-it-works__heading">
            <FontAwesomeIcon icon={faStore} /> {HOW_IT_WORKS.businesses.heading}
          </h3>
          <ol className="home-how-it-works__steps">
            {HOW_IT_WORKS.businesses.steps.map((step, i) => (
              <li key={i}>
                <span className="home-how-it-works__num">{i + 1}</span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
          <Link to="/for-businesses" className="home-cta-secondary">
            Promote Your Business <FontAwesomeIcon icon={faArrowRight} />
          </Link>
        </div>
      </div>
    </R5Section>
  );

  // --- Featured businesses grid (first 3 from the live pool) ---
  const renderFeaturedBusinesses = () => {
    const gridListings = featuredListings.slice(0, 3);
    if (gridListings.length === 0) return null;

    return (
      <R5Section
        className="featured-businesses-section"
        title="Featured Island Businesses"
        meta="Captain's Picks"
        actions={
          <Link to="/business-listings" className="view-all-link">
            View All <FontAwesomeIcon icon={faArrowRight} />
          </Link>
        }
      >
        <div className="r5-grid--featured">
          {gridListings.map((listing, idx) => {
            const isPromoted = listing.promotion?.isActive;
            const badge = isPromoted
              ? PROMOTION_BADGES[listing.promotion.type] || "★ Featured"
              : idx === 0 ? "Captain's Pick" : undefined;

            return (
              <R5Card
                key={listing.id}
                href={`/business-listings/${listing.id}`}
                featured={idx === 0}
                badge={badge}
                imageSrc={listing.images?.[0] || undefined}
                imageAlt={listing.name || ''}
                category={[listing.category, listing.location].filter(Boolean).join(" · ")}
                title={listing.name}
                blurb={listing.description}
                meta={
                  <>
                    <span>{isPromoted ? "★ Promoted" : "★ Featured"}</span>
                    <strong>View Berth →</strong>
                  </>
                }
              />
            );
          })}
        </div>
      </R5Section>
    );
  };

  // --- About section ---
  const renderAboutSection = () => (
    <GlassSection className="about-section-wrapper">
      <div className="about-section">
        <div className="about-content">
          <h2>About The Pirates Guild</h2>
          <p>
            The USVI Pirates Guild was founded to create a vibrant community
            connecting travelers with local businesses throughout the US Virgin
            Islands. Our mission is to enhance the island experience for
            visitors while supporting the local economy.
          </p>
          <p>
            Through our network of partner businesses, members get access to
            local deals, an interactive Treasure Map of hidden island treasures,
            seasonal events, and a Doubloons loyalty program that rewards
            discovery. Local businesses gain visibility, sponsored placement
            options, and a simple commission-only booking system.
          </p>
          <p>
            Whether you're a first-time visitor, a seasonal resident, or a
            local business owner, the Pirates Guild offers valuable resources
            to make the most of your time in paradise.
          </p>
          <Link to="/support" className="learn-more-btn">
            Learn More About Us
          </Link>
        </div>
        <div className="about-image">
          <img src="../../assets/Pirates Guild.jpg" alt="USVI Pirates Guild" />
        </div>
      </div>
    </GlassSection>
  );

  // --- Doubloons teaser (replaces V1 "How tokens work" section) ---
  const renderDoubloonsTeaserSection = () => {
    const cards = [
      {
        icon: faGift,
        title: "Bonus rewards as you explore",
        body: "Doubloons are loyalty points you earn for visiting businesses, attending events, completing treasure hunts, and referring friends.",
      },
      {
        icon: faCoins,
        title: "Not required for standard deals",
        body: "You don't need Doubloons to use Guild deals at participating businesses. Show your free pass and save.",
      },
      {
        icon: faTicket,
        title: "Redeem for fun extras",
        body: "Spend Doubloons on member-only perks, event upgrades, sponsored partner rewards, merch, and limited-time treasure hunt prizes.",
      },
      {
        icon: faHandshake,
        title: "Sponsored by local businesses",
        body: "Businesses can sponsor Doubloon rewards to drive traffic during slow periods — a win-win for travelers and partners.",
      },
    ];

    return (
      <R5Section
        className="home-doubloons-teaser"
        title={DOUBLOONS.title}
        meta="Bonus loyalty rewards — not a required currency"
        actions={
          <Link to="/doubloons" className="view-all-link">
            Learn more <FontAwesomeIcon icon={faArrowRight} />
          </Link>
        }
      >
        <div className="home-value-grid">
          {cards.map((card) => (
            <R5Card key={card.title}>
              <div className="r5-card__body home-value-card__body">
                <FontAwesomeIcon icon={card.icon} className="home-value-card__icon" />
                <h3 className="r5-card__title">{card.title}</h3>
                <p className="r5-card__blurb">{card.body}</p>
              </div>
            </R5Card>
          ))}
        </div>
        <p className="home-doubloons-teaser__disclaimer">{DOUBLOONS.disclaimer}</p>
      </R5Section>
    );
  };

  // --- Member value section (V2: discovery, perks, concierge) ---
  const renderMemberValueSection = () => {
    const memberCards = [
      {
        icon: faTags,
        title: "Local deals, easy to find",
        body: "Member-only offers at participating restaurants, shops, tours, and more — redeemable with your free pass at checkout.",
      },
      {
        icon: faMapMarkedAlt,
        title: "Interactive Treasure Map",
        body: "Discover hidden island treasures, beachside cafes, and tour stops on a map made for explorers, not commuters.",
      },
      {
        icon: faCalendarAlt,
        title: "Events, treasure hunts, meetups",
        body: "Find local happenings, pirate-themed gatherings, food specials, and seasonal experiences across the USVI.",
      },
      {
        icon: faConciergeBell,
        title: "Concierge help (Plus members)",
        body: "Need an itinerary, a reservation, or a last-minute tour booking? Traveler Plus gets concierge assistance and VIP event access.",
      },
      {
        icon: faClock,
        title: "Early access to limited drops",
        body: "First look at limited tastings, charter slots, and seasonal experiences before they open to the wider crew.",
      },
      {
        icon: faGift,
        title: "Earn Doubloons as you go",
        body: "Bonus rewards for visits, referrals, treasure hunts, and bookings — redeem for member-only perks, event upgrades, and partner rewards.",
      },
    ];

    const guestCta = (
      <div className="home-section-cta">
        <Link to={HOME_HERO.primaryCta.href} className="home-cta-primary">
          Join Free as a Traveler <FontAwesomeIcon icon={faArrowRight} />
        </Link>
        <Link to="/traveler-passes" className="home-cta-secondary">
          View Premium Passes
        </Link>
      </div>
    );

    const memberCta = (
      <div className="home-section-cta">
        <Link to="/business-listings" className="home-cta-primary">
          Browse Deals <FontAwesomeIcon icon={faArrowRight} />
        </Link>
        <Link to="/traveler-passes" className="home-cta-secondary">
          {travelerPlus ? "Manage your plan" : "Upgrade for more perks"}
        </Link>
      </div>
    );

    return (
      <R5Section
        className="home-member-value"
        title="Why travelers join the Guild"
        meta="Built for people who actually use the islands"
      >
        <div className="home-value-grid">
          {memberCards.map((card) => (
            <R5Card key={card.title}>
              <div className="r5-card__body home-value-card__body">
                <FontAwesomeIcon icon={card.icon} className="home-value-card__icon" />
                <h3 className="r5-card__title">{card.title}</h3>
                <p className="r5-card__blurb">{card.body}</p>
              </div>
            </R5Card>
          ))}
        </div>
        {isAuthenticated ? memberCta : guestCta}
      </R5Section>
    );
  };

  // --- Business value section ---
  const renderBusinessValueSection = () => {
    const verticals = [
      {
        icon: faUtensils,
        title: "Restaurants",
        body: "Happy-hour deals, complimentary apps, and off-peak fillers that turn first-timers into regulars.",
      },
      {
        icon: faSpa,
        title: "Spas & wellness",
        body: "Discounted services, members-only add-ons, and early access to seasonal treatments.",
      },
      {
        icon: faShip,
        title: "Boat charters",
        body: "Member discounts on day sails and sunset cruises — fill quiet weekday departures with motivated crews.",
      },
      {
        icon: faPersonSwimming,
        title: "Diving operators",
        body: "Bundle pricing on dives, gear rental, and cert courses. Loyal divers come back with friends.",
      },
      {
        icon: faBicycle,
        title: "Bike & gear rentals",
        body: "Hourly and multi-day discounts that move inventory and keep return renters in your pipeline.",
      },
      {
        icon: faMapLocationDot,
        title: "Tours & guides",
        body: "Promote signature tours, private bookings, and seasonal routes directly to active members.",
      },
      {
        icon: faTruckFast,
        title: "Food trucks",
        body: "Loyalty perks, sponsored combos, and event-day boosts that drive lines to your window.",
      },
      {
        icon: faStore,
        title: "Shops & retail",
        body: "Build purchase-discount coupons, member-only drops, and bundled offers redeemable at checkout.",
      },
    ];

    const ownerCta = (
      <div className="home-section-cta">
        <Link to="/business-dashboard" className="home-cta-primary">
          Open your business dashboard <FontAwesomeIcon icon={faArrowRight} />
        </Link>
        <Link to="/edit-business-listing/new" className="home-cta-secondary">
          Add another listing
        </Link>
      </div>
    );

    const guestCta = (
      <div className="home-section-cta">
        <Link to="/for-businesses" className="home-cta-primary">
          List Your Business Free <FontAwesomeIcon icon={faArrowRight} />
        </Link>
        <Link to="/signup?audience=business" className="home-cta-secondary">
          Create your account first
        </Link>
      </div>
    );

    const memberCta = (
      <div className="home-section-cta">
        <Link to="/for-businesses" className="home-cta-primary">
          Add a Business Listing <FontAwesomeIcon icon={faArrowRight} />
        </Link>
        <Link to="/business-listings" className="home-cta-secondary">
          See how partners promote
        </Link>
      </div>
    );

    let cta = guestCta;
    if (business) cta = ownerCta;
    else if (isAuthenticated) cta = memberCta;

    return (
      <R5Section
        className="home-business-value"
        title="Why businesses join the Guild"
        meta="Free listing · paid promotion when you want reach · commission-only bookings"
      >
        <p className="home-business-value__lede">
          Get found by travelers searching for places to eat, drink, dive, sail, and shop
          across the USVI. List free in our directory, upgrade to a Featured Captain or
          Port Sponsor package when you want premium placement, and accept bookings with
          a simple commission split — no monthly booking fees.
        </p>
        <div className="home-business-value__signals">
          <div className="home-business-value__signal">
            <FontAwesomeIcon icon={faChartPie} />
            <span>Track listing views, clicks, and bookings from your dashboard</span>
          </div>
          <div className="home-business-value__signal">
            <FontAwesomeIcon icon={faBullhorn} />
            <span>Sponsored placements & featured spots target active travelers</span>
          </div>
          <div className="home-business-value__signal">
            <FontAwesomeIcon icon={faChartLine} />
            <span>Commission-only bookings via Stripe Connect — no monthly fees</span>
          </div>
        </div>
        <div className="home-business-value__verticals">
          {verticals.map((card) => (
            <R5Card key={card.title}>
              <div className="r5-card__body home-value-card__body">
                <FontAwesomeIcon icon={card.icon} className="home-value-card__icon" />
                <h3 className="r5-card__title">{card.title}</h3>
                <p className="r5-card__blurb">{card.body}</p>
              </div>
            </R5Card>
          ))}
        </div>
        {cta}
      </R5Section>
    );
  };

  // --- Body composition ---
  // Universal lead trio (Featured Businesses → Events → Properties) shows
  // for every viewer state. Doubloons + How-It-Works follow. "Why Travelers
  // Join" and "Why Businesses Join" are gated by role so we don't pitch a
  // viewer on a club they're already in. Admins always see everything.
  const renderBody = () => {
    const showTravelerPitch = isAdmin || !(traveler || travelerPlus);
    const showBusinessPitch = isAdmin || !business;
    return (
      <>
        {/* Signed-in members lead with "what you can do right now"; logged-out
            visitors fall straight into the marketing content below. */}
        {isAuthenticated && <MemberResources />}
        {renderFeaturedBusinesses()}
        <FeaturedEventsCarousel />
        <FeaturedPropertiesCarousel />
        {renderDoubloonsTeaserSection()}
        {renderHowItWorksSection()}
        {showTravelerPitch && renderMemberValueSection()}
        {showBusinessPitch && renderBusinessValueSection()}
        {renderAboutSection()}
      </>
    );
  };

  return (
    <>
      <SEO
        title={PAGE_META.home.title}
        description={PAGE_META.home.description}
        url="/"
      />
      <StructuredData data={generateOrganizationSchema()} />
      {renderHero()}
      {isMemberWithSubscription && (
        <div className="home-member-cta-wrap">
          {renderMemberAdventuresCta()}
        </div>
      )}
      <div className="home-page page-container">
        <div className="home-staff-entry" role="region" aria-label="Staff access">
          <span>Business staff?</span>
          <Link to="/staff/login" className="home-staff-entry__link">
            Staff Sign-in Portal &rarr;
          </Link>
        </div>
        {loading ? (
          <div className="loading-spinner">
            <FontAwesomeIcon icon={faAnchor} spin />
            <p>Loading Pirates Guild Hub...</p>
          </div>
        ) : (
          <>
            {renderBody()}
          </>
        )}
        <p className="home-staff-portal-link">
          Business staff member?{' '}
          <Link to="/staff/login">Sign in to the Staff Portal →</Link>
        </p>
      </div>
    </>
  );
}

export default HomePage;
